import { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Button from '@components/Button';
import FiltersModal from '@components/FiltersModal';
import { Chip } from '@components/Chip';
import { FiltersFormCreator } from '@components/FiltersFormCreator';
import { FiltersIcon } from 'assets/images/icons';
import { getChipsLabel, createUpdatedChips, isChipActive, hasActiveChips } from '@helpers/filters';
import { AGE_BRACKETS, SEX_LABEL_KEYS, Sex, languageOptions, monthOptions } from '@helpers/contact-demographics';
import { formatTimeframeChipValue } from '@helpers/dates';
import { useTranslation, useContactLocations, useIntlLocale } from '@hooks';

const Modal = styled(FiltersModal)({
    '& #pl-filters-contacts .timeframe-picker': {
        gridColumn: '1 / span 2',
        '@media (max-width: 1024px)': {
            gridColumn: '1 / span 1',
        },
    },
    '& #pl-filters-contacts .multiselect-labels': {
        gridColumn: '1 / span 2',
        '@media (max-width: 1024px)': {
            gridColumn: '1 / span 1',
        },
    },
});

export const FiltersWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    '@media print': {
        '& button': {
            display: 'none',
        },
    },
});

const FiltersPanel = ({
    className,
    setFiltersOpened,
    filtersNumber,
    filterOpened,
    onApplyFilters,
    disabled = false,
    contactLabels = [],
    filter,
    setFilter,
    withChips = true,
    type,
    setFilterNumber,
}) => {
    const firstRender = useRef(true);
    const { t } = useTranslation();
    const intlLocale = useIntlLocale();
    const language = useSelector(state => state.auth.user?.language);
    const [filterChips, setFilterChips] = useState({
        companies: [],
        contactLabels: [],
        timeframe: {},
        sexes: [],
        languages: [],
        birthMonths: [],
        ageBracket: [],
    });
    const [refetchFilters, setRefetchFilters] = useState(false);

    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const { getLocationsOptions } = useContactLocations();

    useEffect(() => {
        if (refetchFilters) {
            setRefetchFilters(false);
            applyChips();
            onApplyFilters();
        }
    }, [refetchFilters]);

    useEffect(() => {
        if (firstRender.current && filter.companies.length > 0) {
            applyChips();
            firstRender.current = false;
        }
    }, [filter]);

    const languages = useMemo(() => languageOptions(intlLocale), [intlLocale]);
    const birthMonths = useMemo(() => monthOptions(intlLocale), [intlLocale]);

    const options = useMemo(() => {
        //locationsOptions
        const locationsOptions = getLocationsOptions();

        //contactLabelsOptions
        const contactLabelsOptions = contactLabels.map(item => ({ value: item.id, label: item.name }));

        return {
            companies: locationsOptions,
            contactLabels: contactLabelsOptions,
            sexes: Object.values(Sex).map(value => ({ value, label: t(SEX_LABEL_KEYS[value]) })),
            languages,
            birthMonths,
            ageBracket: AGE_BRACKETS.map(bracket => ({ value: bracket.value, label: bracket.value })),
        };
    }, [contactLabels, getLocationsOptions, languages, birthMonths, t]);

    useEffect(() => {
        const allFilters = { ...filter, ...filterChips };
        const filterNumber = Object.keys(allFilters).reduce((total, key) => {
            let increment = 0;

            switch (key) {
                case 'timeframe':
                    increment = filterChips[key]?.start ? 1 : 0;
                    break;
                default:
                    increment = filterChips[key]?.length > 0 ? 1 : 0;
                    break;
            }

            return total + increment;
        }, 0);

        setFilterNumber(filterNumber);
    }, [filterChips, filter]);

    const update = {
        companies: items => {
            const selectedValues = items.length ? items : [];
            setFilter(prev => ({ ...prev, companies: selectedValues }));
        },
        contactLabels: value => {
            setFilter(prev => ({ ...prev, contactLabels: value }));
        },
        timeframe: data => {
            setFilter(prev => ({ ...prev, timeframe: { start: data[0], end: data[1] } }));
        },
        sexes: value => setFilter(prev => ({ ...prev, sexes: value })),
        languages: value => setFilter(prev => ({ ...prev, languages: value })),
        birthMonths: value => setFilter(prev => ({ ...prev, birthMonths: value })),
        ageBracket: value => setFilter(prev => ({ ...prev, ageBracket: value })),
    };

    const CHIPS_ACTIONS = {
        companies: {
            reset: () => update.companies(options.companies),
            value: getChipsLabel(filterChips?.companies),
        },
        contactLabels: {
            reset: () => setFilter(prev => ({ ...prev, contactLabels: [] })),
            value: getChipsLabel(filterChips?.contactLabels),
        },
        sexes: {
            reset: () => setFilter(prev => ({ ...prev, sexes: [] })),
            value: getChipsLabel(filterChips?.sexes),
        },
        languages: {
            reset: () => setFilter(prev => ({ ...prev, languages: [] })),
            value: getChipsLabel(filterChips?.languages),
        },
        birthMonths: {
            reset: () => setFilter(prev => ({ ...prev, birthMonths: [] })),
            value: getChipsLabel(filterChips?.birthMonths),
        },
        ageBracket: {
            reset: () => setFilter(prev => ({ ...prev, ageBracket: '' })),
            value: getChipsLabel(filterChips?.ageBracket),
        },
        timeframe: {
            reset: () => setFilter(prev => ({ ...prev, timeframe: { start: null, end: null } })),
            value: filterChips?.timeframe?.start
                ? formatTimeframeChipValue(filterChips.timeframe.start, filterChips.timeframe.end, language)
                : '',
        },
    };

    const applyChips = () => {
        const updatedChips = createUpdatedChips(filterChips, filter, options);
        setFilterChips(updatedChips);
    };

    const resetFilters = () => Object.values(CHIPS_ACTIONS).forEach(({ reset }) => reset());

    const createFormOptions = (filter, update, options) => {
        const formOptions = [];

        const ArrayKeyMap = {
            companies: 'autocomplete',
            contactLabels: 'multiSelectLabels',
        };

        for (let key in filter) {
            if (Array.isArray(filter[key])) {
                formOptions.push({
                    type: ArrayKeyMap[key] || 'multiSelect',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: update[key],
                    options: options[key],
                    countable: true,
                    error: false,
                });
            } else if (key === 'ageBracket') {
                formOptions.push({
                    type: 'select',
                    label: t(`filters.${key}`),
                    value: filter[key],
                    onChange: event => update[key](event.target.value),
                    options: [{ value: '', label: t('filters.anyAge') }, ...options[key]],
                });
            } else if (key === 'timeframe') {
                formOptions.push({
                    type: 'timeframe',
                    label: t(`filters.${key}`),
                    value: [filter[key].start, filter[key].end],
                    onChange: update[key],
                });
            }
        }

        return formOptions;
    };

    const isValid = true;

    const hasDraftFilters = useMemo(
        () => !isValid || hasActiveChips(createUpdatedChips(filterChips, filter, options)),
        [isValid, filterChips, filter, options],
    );

    return (
        <FiltersWrapper>
            <Button
                className={className}
                variant="outlined"
                onClick={() => setFiltersOpened(true)}
                startIcon={<FiltersIcon />}
                disabled={disabled}
                sx={{ marginRight: '8px' }}
            >
                {t('buttons.filters')}
                {filtersNumber > 0 ? ` (${filtersNumber})` : ''}
            </Button>
            {withChips &&
                !isMobile &&
                Object.keys(filterChips).map(
                    key =>
                        isChipActive(filterChips[key]) && (
                            <Chip
                                key={key}
                                label={CHIPS_ACTIONS[key].value}
                                onDelete={() => {
                                    CHIPS_ACTIONS[key].reset();
                                    setRefetchFilters(true);
                                }}
                            />
                        ),
                )}
            <Modal
                isOpen={filterOpened}
                handleClose={() => setFiltersOpened(false)}
                onApply={() => {
                    setRefetchFilters(true);
                    setFiltersOpened(false);
                }}
                applyDisabled={!isValid}
                onClear={resetFilters}
                clearDisabled={!hasDraftFilters}
            >
                <FiltersFormCreator type={type} options={createFormOptions(filter, update, options)} />
            </Modal>
        </FiltersWrapper>
    );
};

export default FiltersPanel;
